import { useEffect, useRef } from 'react';

import { useListingState } from '@/hooks/useListingState';

/**
 * useFiltersModalScroll
 *
 * Custom hook to control scroll behaviour for the Filters Modal inside an iframe.
 *
 * Features,
 * - Disable scrolling of iframe's body when the modal is open
 * - Allow scrolling within the modal body, until it reaches the top or bottom
 * - When modal-body reaches the scroll boundary, pass the scroll to the parent page of iframe
 * - Support both wheel scroll (desktop) and touch gestures (mobile)
 * - Observes modal-body height in real time
 *
 * @param elementId The HTML element id of modal body
 */

export function useFiltersModalScroll(elementId: string) {
  const touchStartY = { current: 0 };
  const clientHeightRef = useRef(0);
  const { modalState } = useListingState();
  const isActive = modalState.status === 'OPEN';

  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    if (!elementId) return;

    const el = document.getElementById(elementId) as HTMLElement | null;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      const atTop = el.scrollTop === 0;
      const atBottom =
        Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
      const goingDown = e.deltaY > 0;
      const goingUp = e.deltaY < 0;

      const shouldScrollParent = (goingDown && atBottom) || (goingUp && atTop);

      if (shouldScrollParent) {
        window.parent.scrollBy(0, e.deltaY);
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const diffY = touchStartY.current - currentY;

      const atTop = el.scrollTop === 0;
      const atBottom =
        Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
      const goingDown = diffY > 0;
      const goingUp = diffY < 0;

      const shouldScrollParent = (goingDown && atBottom) || (goingUp && atTop);

      if (shouldScrollParent) {
        window.parent.scrollBy(0, diffY);
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        clientHeightRef.current = entry.target.clientHeight;
      }
    });

    observer.observe(el);

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';

      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);

      observer.disconnect();
    };
  }, [isActive, elementId]);
}
